import { POrderLineWithHeaderDto } from '../../../../../types/purchaseOrderLines';
import { purchaseOrderLinesService } from '../../../../../services/purchaseOrderLinesService';

/**
 * Export purchase order lines to Excel (CSV format)
 * This function generates a CSV file and triggers a download
 *
 * @param data Array of purchase order lines to export
 * @param includeExtendedColumns Whether to include extended columns
 */
export const exportToExcel = (
  data: POrderLineWithHeaderDto[],
  includeExtendedColumns: boolean = false
): void => {
  // Define CSV headers based on which columns to include
  const essentialHeaders = [
    'Ordre (ERP)',
    'Linjenr (ERP)',
    'Varenr',
    'Beskrivelse',
    'Leverandør nr',
    'Leverandør navn',
    'Bestilt antall',
    'Levert antall',
    'Gjenstår',
    'Framdrift %',
    'Enhet',
    'Ordredato',
    'Leveringsdato',
    'Ordrestatus',
    'Linjestatus',
  ];

  const extendedHeaders = includeExtendedColumns
    ? [
        'Pris',
        'Rabatt %',
        'Lager',
        'Batch-styrt',
        'Serienr-styrt',
        'Standardlokasjon',
        'Vår ref',
        'Deres ref',
        'Varegruppe 1',
        'Varegruppe 2',
        'Varegruppe 3',
        'Strekkode',
        'Batch-nummer',
        'Leverandørens ordrenr',
        'Ordre opprettet',
        'Ordre sist oppdatert',
        'Linje opprettet',
        'Linje sist oppdatert',
      ]
    : [];

  const headers = [...essentialHeaders, ...extendedHeaders];

  // Convert data to CSV rows
  const rows = data.map((line) => {
    const completionPercentage = purchaseOrderLinesService.calculateCompletionPercentage(
      line.quantity,
      line.quantityDelivered
    );

    const essentialRow = [
      line.orderNoERP || '',
      line.lineNoERP?.toString() || line.lineNo.toString(),
      line.itemNo || '',
      line.description1 || '',
      line.supplierNo || '',
      line.supplierName || '',
      line.quantity?.toString() || '0',
      line.quantityDelivered?.toString() || '0',
      line.remainingQuantity?.toString() || '0',
      completionPercentage.toString(),
      line.itemUnitTxt || line.itemUnitCode || '',
      line.orderDate ? new Date(line.orderDate).toLocaleDateString('no-NO') : '',
      line.deliveryDate ? new Date(line.deliveryDate).toLocaleDateString('no-NO') : '',
      line.orderStatusText || '',
      line.lineStatusText || '',
    ];

    const extendedRow = includeExtendedColumns
      ? [
          line.price?.toString() || '',
          line.discount?.toString() || '',
          line.warehouse || '',
          line.batchItemYesNo ? 'Ja' : 'Nei',
          line.serialNoItemYesNo ? 'Ja' : 'Nei',
          line.stdLocationName || '',
          line.yourRef || '',
          line.ourRef || '',
          line.itemGroup1 || '',
          line.itemGroup2 || '',
          line.itemGroup3 || '',
          line.itemBarCode || '',
          line.itemBatchNo || '',
          line.supplierOrderNo || '',
          line.orderCreated
            ? new Date(line.orderCreated).toLocaleString('no-NO')
            : '',
          line.orderLastUpdate
            ? new Date(line.orderLastUpdate).toLocaleString('no-NO')
            : '',
          line.lineCreated ? new Date(line.lineCreated).toLocaleString('no-NO') : '',
          line.lineLastUpdate
            ? new Date(line.lineLastUpdate).toLocaleString('no-NO')
            : '',
        ]
      : [];

    return [...essentialRow, ...extendedRow];
  });

  // Generate CSV content
  const csvContent = [
    headers.join(';'), // Header row
    ...rows.map((row) =>
      row.map((cell) => escapeCsvValue(cell.toString())).join(';')
    ),
  ].join('\n');

  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csvContent;

  // Create blob and download
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `bestillingslinjer_${timestamp}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Escape CSV values to handle commas, quotes, and newlines
 *
 * @param value The value to escape
 * @returns Escaped value
 */
const escapeCsvValue = (value: string): string => {
  // If value contains semicolon, comma, quote, or newline, wrap in quotes
  if (
    value.includes(';') ||
    value.includes(',') ||
    value.includes('"') ||
    value.includes('\n')
  ) {
    // Escape existing quotes by doubling them
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Export to Excel format using HTML table (alternative approach)
 * This can be used if more complex Excel features are needed
 *
 * @param data Array of purchase order lines to export
 * @param includeExtendedColumns Whether to include extended columns
 */
export const exportToExcelHTML = (
  data: POrderLineWithHeaderDto[],
  includeExtendedColumns: boolean = false
): void => {
  const essentialHeaders = [
    'Ordre (ERP)',
    'Linjenr (ERP)',
    'Varenr',
    'Beskrivelse',
    'Leverandør nr',
    'Leverandør navn',
    'Bestilt antall',
    'Levert antall',
    'Gjenstår',
    'Framdrift %',
    'Enhet',
    'Ordredato',
    'Leveringsdato',
    'Ordrestatus',
    'Linjestatus',
  ];

  const extendedHeaders = includeExtendedColumns
    ? [
        'Pris',
        'Rabatt %',
        'Lager',
        'Batch-styrt',
        'Serienr-styrt',
        'Standardlokasjon',
        'Vår ref',
        'Deres ref',
      ]
    : [];

  const headers = [...essentialHeaders, ...extendedHeaders];

  // Build HTML table
  let html = '<html><head><meta charset="utf-8"></head><body><table>';
  html += '<thead><tr>';
  headers.forEach((header) => {
    html += `<th>${header}</th>`;
  });
  html += '</tr></thead><tbody>';

  data.forEach((line) => {
    const completionPercentage = purchaseOrderLinesService.calculateCompletionPercentage(
      line.quantity,
      line.quantityDelivered
    );

    html += '<tr>';
    html += `<td>${line.orderNoERP || ''}</td>`;
    html += `<td>${line.lineNoERP || line.lineNo}</td>`;
    html += `<td>${line.itemNo || ''}</td>`;
    html += `<td>${line.description1 || ''}</td>`;
    html += `<td>${line.supplierNo || ''}</td>`;
    html += `<td>${line.supplierName || ''}</td>`;
    html += `<td>${line.quantity || 0}</td>`;
    html += `<td>${line.quantityDelivered || 0}</td>`;
    html += `<td>${line.remainingQuantity || 0}</td>`;
    html += `<td>${completionPercentage}</td>`;
    html += `<td>${line.itemUnitTxt || line.itemUnitCode || ''}</td>`;
    html += `<td>${
      line.orderDate ? new Date(line.orderDate).toLocaleDateString('no-NO') : ''
    }</td>`;
    html += `<td>${
      line.deliveryDate ? new Date(line.deliveryDate).toLocaleDateString('no-NO') : ''
    }</td>`;
    html += `<td>${line.orderStatusText || ''}</td>`;
    html += `<td>${line.lineStatusText || ''}</td>`;

    if (includeExtendedColumns) {
      html += `<td>${line.price || ''}</td>`;
      html += `<td>${line.discount || ''}</td>`;
      html += `<td>${line.warehouse || ''}</td>`;
      html += `<td>${line.batchItemYesNo ? 'Ja' : 'Nei'}</td>`;
      html += `<td>${line.serialNoItemYesNo ? 'Ja' : 'Nei'}</td>`;
      html += `<td>${line.stdLocationName || ''}</td>`;
      html += `<td>${line.yourRef || ''}</td>`;
      html += `<td>${line.ourRef || ''}</td>`;
    }

    html += '</tr>';
  });

  html += '</tbody></table></body></html>';

  // Create blob and download
  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `bestillingslinjer_${timestamp}.xls`;

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
